import React, { createContext, useContext, useState } from 'react';

interface ModalsContextProps {
  auth: {
    isOpen: boolean;
    open: (onClose?: () => void) => void;
    close: () => void;
  };
}

const ModalsContext = createContext<ModalsContextProps>({
  auth: {
    isOpen: false,
    open: () => {},
    close: () => {},
  },
});

export const useModals = () => useContext(ModalsContext);

type ModalName = 'auth';

interface ModalsProviderProps {
  children: React.ReactNode;
}

export const ModalsProvider: React.FC<ModalsProviderProps> = ({ children }) => {
  const [modals, setModals] = useState({
    auth: { open: false, onClose: () => {} },
  });

  const openModal = (modalName: ModalName, onClose: () => void = () => {}) => {
    setModals(prevModals => {
      const modal = prevModals[modalName];

      return {
        ...prevModals,
        [modalName]: {
          ...modal,
          open: true,
          onClose,
        },
      };
    });
  };

  const closeModal = (modalName: ModalName) => {
    setModals(prevModals => {
      const modal = prevModals[modalName];

      if (modal && typeof modal.onClose === 'function') {
        setTimeout(() => {
          modal.onClose();
        }, 300);
      }

      return {
        ...prevModals,
        [modalName]: {
          ...modal,
          open: false,
        },
      };
    });
  };

  const openAuthModal = (onClose?: () => void) => openModal('auth', onClose);
  const closeAuthModal = () => closeModal('auth');

  const contextValue = {
    auth: {
      isOpen: modals.auth.open,
      open: openAuthModal,
      close: closeAuthModal,
    },
  };

  return (
    <ModalsContext.Provider value={contextValue}>{children}</ModalsContext.Provider>
  );
};
